import { EtaTemplateCache } from "@/controllers/eta-renderer/eta-template-cache";
import { EtaRenderHost, ObserveShadowRoot } from "@/controllers/eta-renderer/eta-types";
import eta from "@/templating/eta";
import morph from "nanomorph";

type RenderOptions = {
  fromMutation: boolean;
  dirtyRoots? : Set<HTMLElement> | null;
};

type TemplateEntry = { src: string; fn?: ReturnType<typeof eta.compile> };

export class EtaDomRenderer {
  private host              : EtaRenderHost;
  private cache             : EtaTemplateCache;
  private observeShadowRoot?: ObserveShadowRoot;

  constructor(host: EtaRenderHost, cache: EtaTemplateCache, observeShadowRoot?: ObserveShadowRoot) {
    this.host = host;
    this.cache = cache;
    this.observeShadowRoot = observeShadowRoot;
  }

  async render(context: object, { fromMutation, dirtyRoots }: RenderOptions) {
    const roots = dirtyRoots && dirtyRoots.size ? Array.from(dirtyRoots) : [this.host as unknown as HTMLElement];

    const visitWithShadow = async (root: Element) => {
      await this._visit(root, context, fromMutation);
      if (root.hasAttribute("shadow") && (root as HTMLElement).shadowRoot) {
        await this._visit((root as HTMLElement).shadowRoot as ShadowRoot, context, fromMutation);
      }
    };

    for (const r of roots) {
      if (!r) continue;
      await visitWithShadow(r);
    }
  }

  private async _visit(root: Element | ShadowRoot, context: object, fromMutation: boolean) {
    let processed = 0;
    for (const child of Array.from(root.children)) {
      if (!(child instanceof HTMLElement)) continue;

      await this._waitForCustomUpdate(child);

      const shadowRoot = child.hasAttribute("shadow") ? child.shadowRoot : null;
      const traverseShadow = async () => {
        if (shadowRoot) {
          await this._visit(shadowRoot, context, fromMutation);
        }
      };
      if (shadowRoot && this.observeShadowRoot) {
        this.observeShadowRoot(shadowRoot, child);
      }

      const currentSrc = child.innerHTML ?? "";

      await this._renderEtaAttributes(child, context, fromMutation);

      if (this.host.isStateNodeElement(child) && child !== this.host) {
        continue; // nested provider; let it handle its subtree
      }

      const hasNestedStateNode = this._hasNestedStateNode(child);
      if (hasNestedStateNode) {
        await this._visit(child, context, fromMutation);
        await traverseShadow();
        continue;
      }

      if (this._hasRenderNodes(child)) {
        await this._visit(child, context, fromMutation);
        await traverseShadow();
        continue;
      }

      if (child.hasAttribute("data-store-render")) {
        continue;
      }

      const hadTemplate = this.cache.hasTemplate(child);
      const noAttrs = this.cache.hasNoEtaAttrs(child);
      if (!currentSrc.includes("{{") && noAttrs && !hadTemplate && !hasNestedStateNode) {
        await traverseShadow();
        continue;
      }

      const entry = this.cache.updateElementEntry(child, currentSrc, { allowMutationRefresh: fromMutation });

      if (!fromMutation) {
        const out = this._safeEtaRender(entry, context, { onErrorReturnInput: true });
        await this._morphElementFromHTML(child, out);
      }
      await traverseShadow();

      if ((++processed % 50) === 0) {
        await Promise.resolve();
      }
    }
  }

  private async _waitForCustomUpdate(el: HTMLElement) {
    const maybe = (el as unknown as { updateComplete?: Promise<unknown> }).updateComplete;
    if (maybe && typeof (maybe as Promise<unknown>).then === "function") {
      try {
        await maybe;
      } catch { /* ignore */ }
    }
  }

  private _safeEtaRender(entry: TemplateEntry, data: object, { onErrorReturnInput = false }: { onErrorReturnInput?: boolean } = {}) {
    try {
      if (!entry.fn) {
        entry.fn = eta.compile(entry.src);
      }
      const out = eta.render(entry.fn, data);
      return typeof out === "string" ? out : String(out ?? "");
    } catch (err) {
      console.error("Eta render error:", err);
      return onErrorReturnInput ? entry.src : "";
    }
  }

  private async _renderEtaAttributes(el: HTMLElement, data: object, fromMutation: boolean) {
    const names = el.getAttributeNames();
    const cache = this.cache.getOrCreateAttrCache(el);

    if (this.cache.hasNoEtaAttrs(el)) return;

    let foundTemplateAttr = false;
    for (const a of names) {
      const raw = el.getAttribute(a);
      if (!raw || !raw.includes("{{")) continue;
      foundTemplateAttr = true;
      const key = `imp:${a}`;
      let entry = cache.get(key);
      if (!entry || entry.src !== raw) {
        entry = { src: raw };
        cache.set(key, entry);
      }
      if (!fromMutation) {
        const rendered = this._safeEtaRender(entry, data, { onErrorReturnInput: true });
        if (rendered !== raw) {
          try { el.setAttribute(a, rendered); } catch { /* ignore */ }
        }
      }
    }

    if (!foundTemplateAttr) {
      this.cache.markNoEtaAttrs(el);
    }
  }

  private _hasRenderNodes(el: Element): boolean {
    const h = el as HTMLElement;
    if (!h) return false;
    if (h.hasAttribute && h.hasAttribute("data-store-render")) return true;
    const html = h.innerHTML;
    return typeof html === "string" && html.includes("data-store-render");
  }

  private _hasNestedStateNode(el: Element): boolean {
    return !!(el as Element).querySelector?.("bd-state,bd-product,bd-option,bd-context");
  }

  private async _morphElementFromHTML(el: HTMLElement, html: string) {
    if (el.innerHTML === html) return;
    try {
      const wrapper = el.cloneNode(false) as HTMLElement;
      wrapper.innerHTML = html;
      morph(el, wrapper);
    } catch {
      el.innerHTML = html;
    }
  }
}
